import {
  Box,
  makeStyles,
  Divider,
  Typography,
  useTheme,
  Dialog
} from '@material-ui/core';
import {
  SETVIImage,
  SHighlightText,
  SCheckbox,
  SText
} from '@setvi/shared/components';
import { Skeleton } from '@material-ui/lab';
import { TextColor } from '@setvi/shared/enums';
import { QuoteProductDTO } from '@setvi/shared/services/react-query';
import { ImagePreview, ProductDetails } from 'componentsV2';
import { memo, useState } from 'react';
import { useProductStyles } from 'pages/products';
import { useAppContext } from 'Providers/AppProvider/AppContext';

import {
  CompanyProductUserType,
  CompanySettingsTypeID
} from '@setvi/shared/services/react-query/query/user/types';
import { AnalyticsModules, useAnalytics } from '@setvi/shared/hooks';
import { ProductActionType } from 'pages/products/utils';
import { FirstRowBox, RowContainer, SecondRowBox } from '../styled';

const useStyles = makeStyles(() => ({
  container: {
    display: 'flex',
    gap: '12px',
    alignItems: 'flex-start',
    flex: 1
  },
  checkbox: {
    flexShrink: 0
  },
  imageWrapper: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    gap: '4px',
    flexShrink: 0
  },
  imageContainer: {
    position: 'relative',
    flexShrink: 0
  },
  badgePill: {
    zIndex: 2,
    backgroundColor: '#F8F9FA',
    borderRadius: '16px',
    padding: '4px 8px',
    minWidth: '64px',
    height: 24,
    width: 82,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center'
  },
  badgeText: {
    fontSize: '12px',
    fontWeight: 600,
    color: '#5D7083',
    textAlign: 'center',
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    maxWidth: '60px'
  },
  productDetails: {
    flex: 1,
    display: 'flex',
    gap: '12px',
    flexDirection: 'column',
    justifyContent: 'center',
    minWidth: 0,
    height: 76,
    paddingTop: 2
  }
}));

interface ProductInfoProps {
  product: QuoteProductDTO;
  searchQuery?: string;
  onClick?: () => void;
  /** Enable checkbox selection mode */
  selectable?: boolean;
  /** Whether the item is selected (only used when selectable is true) */
  selected?: boolean;
  /** Callback when selection changes (only used when selectable is true) */
  onSelectionChange?: (selected: boolean) => void;
  /** Optional badge text to display below the image (e.g., "Option", "Accessory") */
  badgeText?: string;
  embeded?: boolean;
  isRfqQuote?: boolean;
}

/**
 * Shared component for displaying product information
 * Reusable across product rows, dialogs, and other places in quotes-v2
 */
export const ProductInfo = memo(
  ({
    product,
    searchQuery,
    onClick,
    selectable = false,
    selected = false,
    onSelectionChange,
    badgeText,
    embeded = false,
    isRfqQuote = false
  }: ProductInfoProps) => {
    const classes = useStyles();
    const { palette } = useTheme();
    const { captureAnalytics } = useAnalytics();
    const { companyData } = useAppContext();

    const productClasses = useProductStyles();
    const [selectedProduct, setSelectedProduct] =
      useState<QuoteProductDTO>(null);

    const userType = companyData.Settings[CompanySettingsTypeID.CompanyType]
      ?.Value as CompanyProductUserType;

    const placeholderImage = isRfqQuote
      ? '/images/empty/no-product.png'
      : '/images/product-placeholder.svg';

    const openProductDetails = (p: QuoteProductDTO) => {
      if (p.isFreeHand) return;

      setSelectedProduct({ ...p, _id: p.productId });

      captureAnalytics(
        {
          productSKU: p.sku,
          actionType: ProductActionType.DetailsView
        },
        AnalyticsModules.QuotesCatalog
      );
    };

    const isEnriched = (product as any)._enriched;
    const hasImage = product?.imageUrl || product?.images?.[0]?.path;
    const imageSrc = hasImage || placeholderImage;

    // Truncate badge text if too long
    const truncatedBadge =
      badgeText && badgeText.length > 10
        ? `${badgeText.slice(0, 9)}..`
        : badgeText;

    const imageContent =
      !isEnriched && !hasImage ? (
        <Box
          minWidth={76}
          minHeight={76}
          width={76}
          display="flex"
          alignItems="center"
          justifyContent="center">
          <Skeleton
            variant="rect"
            width={76}
            height={76}
            style={{ borderRadius: 12, zIndex: 2 }}
          />
        </Box>
      ) : (
        <ImagePreview
          title={
            <img
              src={imageSrc}
              alt="product-img"
              onError={e => {
                e.currentTarget.src = placeholderImage;
              }}
              style={{
                width: 180,
                height: 180,
                objectFit: 'contain',
                backgroundColor: palette.common.white,
                borderRadius: 10,
                padding: 20
              }}
            />
          }
          placement="right"
          arrow>
          <Box
            minWidth={76}
            minHeight={76}
            width={76}
            display="flex"
            alignItems="center"
            justifyContent="center">
            <SETVIImage
              src={imageSrc}
              alt={product?.name || 'Product'}
              width={76}
              height={76}
              radius={12}
              fit="contain"
              style={{
                maxHeight: 76,
                maxWidth: 76,
                minHeight: 76,
                zIndex: 2,
                backgroundColor: '#FFFFFF',
                border: '1px solid #AFBAC6'
              }}
            />
          </Box>
        </ImagePreview>
      );

    return (
      <>
        <RowContainer>
          <FirstRowBox>
            <Box className={classes.container}>
              {selectable && (
                <SCheckbox
                  className={classes.checkbox}
                  checked={selected}
                  onChange={() => onSelectionChange?.(!selected)}
                />
              )}
              <Box className={classes.imageContainer}>{imageContent}</Box>
              <Box className={classes.productDetails} onClick={onClick}>
                {!isEnriched && !product.name ? (
                  <Skeleton variant="rect" width="60%" height={16} />
                ) : (
                  <SHighlightText
                    onClick={() => openProductDetails(product)}
                    value={product.name}
                    weight="semibold"
                    searchQuery={searchQuery}
                    maxLines={2}
                    style={{
                      cursor: 'pointer'
                    }}
                  />
                )}
                <Box display="flex" alignItems="center" gridGap={5}>
                  {!isEnriched && !product.sku ? (
                    <Skeleton variant="rect" width={80} height={14} />
                  ) : (
                    <SHighlightText
                      size="sm"
                      fontColor={TextColor.SlateGrey}
                      value={`SKU: ${product.sku}`}
                      searchQuery={searchQuery}
                    />
                  )}
                  <Divider orientation="vertical" flexItem />
                  {!isEnriched && !product?.vendor?.name ? (
                    <Skeleton variant="rect" width={80} height={14} />
                  ) : (
                    <SHighlightText
                      size="sm"
                      fontColor={TextColor.SlateGrey}
                      value={`MFG: ${product?.vendor?.name || 'N/A'}${product?.manufacturerSku ? ` (SKU: ${product.manufacturerSku})` : ''}`}
                      searchQuery={searchQuery}
                    />
                  )}
                  {product?.minimumQuoteQuantity > 1 && (
                    <>
                      <Divider orientation="vertical" flexItem />
                      <SHighlightText
                        size="sm"
                        fontColor={TextColor.SlateGrey}
                        value={`MOQ: ${product?.minimumQuoteQuantity || 'N/A'}`}
                        searchQuery={searchQuery}
                      />
                    </>
                  )}
                </Box>
              </Box>
            </Box>
          </FirstRowBox>
          <SecondRowBox
            hasNote={!!product?.note?.trim()}
            style={
              truncatedBadge
                ? {
                    display: 'flex'
                  }
                : {}
            }>
            {truncatedBadge && (
              <Box className={classes.badgePill}>
                <Typography className={classes.badgeText}>
                  {product?.customLabel
                    ? product?.customLabel
                    : truncatedBadge?.charAt(0)?.toUpperCase() +
                      truncatedBadge?.slice(1)}
                </Typography>
              </Box>
            )}

            {!!product?.note?.trim() && (
              <Box
                style={
                  truncatedBadge
                    ? { flex: 1, marginLeft: 8, marginTop: 6 }
                    : {
                        marginLeft: embeded ? 140 : 88,
                        overflow: 'hidden'
                      }
                }>
                <Divider />
                <SText
                  prefix={`Note: `}
                  title={product?.note}
                  fontColor="#333D47"
                  maxLines={2}
                  style={{
                    padding: '8px 0px 0px'
                  }}
                />
              </Box>
            )}
          </SecondRowBox>
        </RowContainer>

        <Dialog
          maxWidth="md"
          aria-labelledby="product-details-dialog"
          open={!!selectedProduct}
          style={{ zIndex: '1300' }}
          onKeyDown={event => {
            if (event.key === 'Escape') setSelectedProduct(null);
          }}
          classes={{ paper: productClasses.dialogPaper }}>
          {selectedProduct && (
            <ProductDetails
              selectedProduct={selectedProduct}
              selectedCustomerId={undefined}
              userType={userType}
              canSelectProducts={false}
              captureResourceAnalytics={() =>
                captureAnalytics(
                  {
                    productSKU: selectedProduct.sku,
                    actionType: ProductActionType.AssetView
                  },
                  AnalyticsModules.QuotesCatalog
                )
              }
              openProductDetails={openProductDetails}
              onClose={() => setSelectedProduct(null)}
              readOnly
            />
          )}
        </Dialog>
      </>
    );
  }
);

ProductInfo.displayName = 'ProductInfo';
